import { PopupHeader, BroadsiderHudPopup, IconButton, AssetView } from '../';
import {
  StyledItemEditor,
  EditorContent,
  EditorLabel,
  ItemDataSection,
  ItemParams,
  ItemParam,
  ItemList,
  ItemListWrapper,
  ItemAssets,
  ItemAsset,
  InternalItem,
  InternalItemInfo
} from './ItemEditorPopup.styled';
import { TextInput, TextInputMultiline } from '../../components';
import { useMemo, useEffect, useState, useContext } from 'react';
import { getEditorNpcs, updateEditorNpcAvatar } from '../../providers/BasejumpApiProvider';
import { AppContext } from '../../providers/AppContext';
import FigureModel from "../../models/FigureModel";
import MintFigureView from "../../components/MintActionFigures/MintFigureView/MintFigureView";
import { useTokenAuth } from '../../hooks';

const AllAvatarList = ({ avatars, onSelectAvatar, selectedAvatar }) => {
  return (
    <ItemList>
      <ItemListWrapper>
        {avatars.map((avatar, index) => {
          return (
            <InternalItem $selected={selectedAvatar && selectedAvatar.getAvatarId() === avatar.getAvatarId()} onClick={() => onSelectAvatar(avatar)}>
              <InternalItemInfo>{avatar.getName()}</InternalItemInfo>
            </InternalItem>
          )
	})}
      </ItemListWrapper>
    </ItemList>
  );
};

const validateUpdateObject = (updateObject) => {
  if (!updateObject) {
    throw new Error('No update object!');
  }
  if (!updateObject.baseline_stats) {
    throw new Error('Stats cannot be empty!');
  }
  if (!updateObject.baseline_stats.attack_points && updateObject.stats.attack_points !== 0) {
    throw new Error('Attack Points cannot be empty!');
  }
  if (!updateObject.baseline_stats.defence_points && updateObject.stats.defence_points !== 0) {
    throw new Error('Defense Points cannot be empty!');
  }
  if (!updateObject.baseline_stats.health_points && updateObject.stats.health_points !== 0) {
    throw new Error('Health Points cannot be empty!');
  }
  if (!updateObject.baseline_stats.speed && updateObject.stats.speed !== 0) {
    throw new Error('Speed Points cannot be empty!');
  }
  if (!updateObject.baseline_stats.critical_chance && updateObject.stats.critical_chance !== 0) {
    throw new Error('Critical Chance Points cannot be empty!');
  }
};

export const NpcEditor = () => {
  const { setErrorData } = useContext(AppContext);
  const [avatars, setAvatars] = useState([]);
  const { basejumpToken } = useTokenAuth();
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [newAttackPoints, setAttackPoints] = useState('');
  const [newDefensePoints, setDefensePoints] = useState('');
  const [newHealthPoints, setHealthPoints] = useState('');
  const [newSpeedPoints, setSpeedPoints] = useState('');
  const [newCriticalChancePoints, setCriticalChancePoints] = useState('');

  const fetchAvatars = async () => {
    const avatarsFromApi = await getEditorNpcs(basejumpToken);
    const avatars = avatarsFromApi.map(i => (FigureModel.fromBasejumpAvatar(i)));
    setAvatars(avatars);
  };

  useEffect(() => {
    fetchAvatars();
  }, [basejumpToken]);

  useEffect(() => {
    if (selectedAvatar) {
      const {
        attackPoints,
        defencePoints,
        healthPoints,
        criticalChance,
        speed,
      } = selectedAvatar.getBaselineStats();
      setAttackPoints(`${attackPoints}`);
      setDefensePoints(`${defencePoints}`);
      setHealthPoints(`${healthPoints}`);
      setSpeedPoints(`${speed}`);
      setCriticalChancePoints(`${criticalChance}`);
    }
  }, [selectedAvatar]);

  const controls = {};
  const model = useMemo(() => ({
    id: '0000',
    name: 'mock',
    vrm: '/assets/default_avatar.glb',
    glb: '/assets/default_avatar.vrm',
    image: '/assets/avatar.png'
  }), []);
  const onSaveSelectedAvatar = async () => {
    setSaveDisabled(true);
    try {
      const updateObject = {
        baseline_stats: {
          attack_points: parseFloat(newAttackPoints),
          defence_points: parseFloat(newDefensePoints),
          health_points: parseFloat(newHealthPoints),
          speed: parseFloat(newSpeedPoints),
          critical_chance: parseFloat(newCriticalChancePoints),
	}
      };
      validateUpdateObject(updateObject);
      const updatedAvatar = await updateEditorNpcAvatar(basejumpToken, selectedAvatar.getAvatarId(), updateObject);
      await fetchAvatars();
    } catch (err) {
      setErrorData({ title: 'Error', description: err.message });
    } finally {
      setSaveDisabled(false);
    }
  };

  return (
    <EditorContent>
      <MintFigureView controls={controls}
        figure={selectedAvatar || FigureModel.fromAlchemyNft(model)}
        noDecorators
        noEquip
        width='20rem'
        height='100%'
        maxHeight={'100%'}
      />
      <ItemDataSection>
        {selectedAvatar && <>
          <ItemParams key={`params-editor-${selectedAvatar.getAvatarId()}`}>
            <ItemParam>
              <ItemParams>
                <ItemParam>
                  <EditorLabel>Attack Points:</EditorLabel>
                  <TextInput initText={`${selectedAvatar.getBaselineStats().attackPoints}`} onChange={(newPoints) => setAttackPoints(newPoints)}/>
                  <EditorLabel>Defense Points:</EditorLabel>
                  <TextInput initText={`${selectedAvatar.getBaselineStats().defencePoints}`} onChange={(newPoints) => setDefensePoints(newPoints)}/>
                  <EditorLabel>Health Points:</EditorLabel>
                  <TextInput initText={`${selectedAvatar.getBaselineStats().healthPoints}`} onChange={(newPoints) => setHealthPoints(newPoints)}/>
                  <EditorLabel>Speed Points:</EditorLabel>
                  <TextInput initText={`${selectedAvatar.getBaselineStats().speed}`} onChange={(newPoints) => setSpeedPoints(newPoints)}/>
                  <EditorLabel>Crit Points:</EditorLabel>
                  <TextInput initText={`${selectedAvatar.getBaselineStats().criticalChance}`} onChange={(newPoints) => setCriticalChancePoints(newPoints)}/>
                  <IconButton onClick={onSaveSelectedAvatar} disabled={saveDisabled || !selectedAvatar}>
                    Save changes
                  </IconButton>
                </ItemParam>
              </ItemParams>
            </ItemParam>
          </ItemParams>
        </>}
      </ItemDataSection>
      <AllAvatarList avatars={avatars} selectedAvatar={selectedAvatar} onSelectAvatar={(a) => setSelectedAvatar(a)}/>
    </EditorContent>
  );
};

